import {
  ALTO_TO_FUZE_HEALTH_ICON_MAP,
  type AltoIconName,
  type ColorVariant,
  fuzeHealthIconColorVariants,
} from '@alto/design-library-tokens';
import React from 'react';
import { calculateIconPxSize } from './utils';

// union type of the subset of material icons used for FuzeHealth
type MaterialIconName = (typeof ALTO_TO_FUZE_HEALTH_ICON_MAP)[keyof typeof ALTO_TO_FUZE_HEALTH_ICON_MAP];

type Props = {
  readonly allowFontScaling?: boolean;
  readonly accessibilityLabel?: string;
  readonly name: AltoIconName;
  /**
   * Use for testing-library identification purposes, only if you can't rely on user-readable text or a11y labels.
   */
  readonly testID?: string;
  readonly type?: ColorVariant;
};

const getMaterialIconName = (name: AltoIconName): MaterialIconName => {
  let baseIconName = name.split('-')[0];
  // There are variants for checkbox and radio unchecked and checked (duo) states.
  // Preserve checkbox-duo and radio-duo names for the checked state.
  if (name === 'checkbox-duo' || name === 'radio-duo') {
    baseIconName = name;
  }

  return ALTO_TO_FUZE_HEALTH_ICON_MAP[baseIconName as keyof typeof ALTO_TO_FUZE_HEALTH_ICON_MAP];
};

export const FuzeHealthIcon = ({
  accessibilityLabel,
  allowFontScaling = true,
  name,
  testID,
  type = 'primary',
}: Props) => {
  const materialIconName = getMaterialIconName(name);
  if (!materialIconName) {
    throw new Error(`Invalid icon name: ${name}`);
  }
  const size = name.endsWith('-small') ? 'small' : 'regular';
  const sizePx = calculateIconPxSize(size, allowFontScaling);
  const isDuo = name.endsWith('-duo');
  const color = isDuo ? fuzeHealthIconColorVariants[type].duo : fuzeHealthIconColorVariants[type].main;
  const fill = isDuo ? 1 : 0;

  return (
    <span
      aria-label={accessibilityLabel}
      role="img"
      data-testid={testID}
      className="material-symbols-outlined"
      style={{ color, fontSize: `${sizePx}px`, fontVariationSettings: `'FILL' ${fill}` }}
    >
      {materialIconName}
    </span>
  );
};
